import { useMutation } from '@apollo/client';
import React, { lazy, Suspense, useContext } from 'react';
import { useToggle } from 'react-use';
import { useAsync } from 'hooks/use-async.hook';
import {
  Alert,
  Button,
  Checkbox,
  Link,
  Notification,
  Text,
} from '@perch-labs/shrike';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { copyFor } from 'config';
import { MappedContractSubscriberRequirement } from 'hooks/use-contract-requirements.hook';
import { translateError } from 'utils/errors/translate-error';
import { Modal } from '../modal';
import { LinkWrapper } from './vnm-terms.style';
import { TermLink } from './term-link';
import { CREATE_CONTRACT_AGREEMENTS } from './vnm-terms.api';

const ScrollableTerms = lazy(() => import('./scrollable-terms'));

const getCopy = copyFor('modules.notification.vnmTerms');

export interface VnmTermsProps {
  incompleteRequirements: MappedContractSubscriberRequirement[];
  title: string;
  userId: number;
  onSubmit?: () => Promise<any>;
  onClose: () => void;
  withModal?: boolean;
}

export const VnmTerms = ({
  incompleteRequirements,
  userId,
  title,
  onSubmit = () => Promise.resolve(),
  onClose,
  withModal = true,
}: VnmTermsProps) => {
  const [termsChecked, setTermsChecked] = useToggle(false);
  const scrollRequiredContract = incompleteRequirements.find(
    r => r.scrollRequired
  );
  const [termsDisabled, setTermsDisabled] = useToggle(!!scrollRequiredContract);

  const links = incompleteRequirements.map((req, index) => (
    <TermLink
      key={req.contractVersionId}
      contractVersion={req}
      index={index}
      numberOfContracts={incompleteRequirements.length}
    />
  ));

  const [mutate] = useMutation<CreateContractAgreementsMutation>(
    CREATE_CONTRACT_AGREEMENTS
  );
  const { addNotification } = useContext(Notification.Context);

  const [submitResult, submit] = useAsync(async () => {
    const input = incompleteRequirements.map(r => ({
      contractVersionId: r.contractVersionId,
      contractVersionLanguageId: r.contractVersionLanguage?.id,
      userAgent: window.navigator.userAgent,
      userId,
    }));
    await mutate({
      variables: {
        input,
      },
    });
    await onSubmit();
    addNotification({
      displayTime: NOTIFICATION_DISPLAY_TIME,
      message: getCopy('notification'),
      onAdd: onClose,
      variant: 'success',
    });
  });

  const handleSubmit = e => {
    e.preventDefault();
    submit();
  };

  const content = (
    <form onSubmit={handleSubmit}>
      <Text tag="h2" textStyle="heading700">
        {title}
      </Text>
      <Text opacity="medium" margin="16px 0">
        {scrollRequiredContract
          ? getCopy('description.withScroll')
          : getCopy('description.withoutScroll')}
      </Text>
      {scrollRequiredContract && (
        <Suspense fallback={<div>Loading...</div>}>
          <ScrollableTerms
            contractVersion={scrollRequiredContract}
            checkTerms={() => setTermsChecked(true)}
            onScrollToBottom={() => setTermsDisabled(false)}
          />
        </Suspense>
      )}
      <Checkbox
        disabled={termsDisabled}
        checked={termsChecked}
        onChange={setTermsChecked}
        margin={{ bottom: '32px', top: '32px' }}
      >
        <Text>
          {getCopy('checkbox.description')[0]}
          {links}
          {getCopy('checkbox.description')[1]}
        </Text>
      </Checkbox>
      <Alert margin={{ bottom: '16px' }}>
        {translateError(submitResult.error)}
      </Alert>
      <Button
        disabled={!termsChecked}
        fullWidth
        loading={submitResult.loading}
        type="submit"
      >
        {getCopy('buttons.submit')}
      </Button>
      {scrollRequiredContract && (
        <LinkWrapper>
          <Link
            href={scrollRequiredContract.fileUrl}
            download={true}
            type="standalone"
          >
            {getCopy('links.download')}
          </Link>
        </LinkWrapper>
      )}
    </form>
  );
  if (withModal)
    return (
      <Modal width={600} onClose={onClose}>
        {content}
      </Modal>
    );
  return content;
};
